/**
 * useMapLayerDoubleTap Hook - Double-tap detection for MapLibre layers
 *
 * Handles double-tap gesture on MapLibre GL JS layers (Line, Shape geometries).
 * Uses MapLibre layer events: map.on('touchstart', layerId, ...)
 *
 * Features:
 * - Long-press filtering (ignores taps > maxTapDuration)
 * - Optional double-tap zoom protection (prevents map zoom on double-tap)
 * - Optional single-tap callback
 *
 * @module hooks/gestures/useMapLayerDoubleTap
 * @example
 * ```typescript
 * useMapLayerDoubleTap(interactiveLayerId, {
 *   preventZoom: true,
 *   onDoubleTap: () => openTaskForEdit(taskId, geometryId),
 *   onSingleTap: () => navigateToTaskDetails(taskId),
 * });
 * ```
 */

import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import {
  INTERACTION_DOUBLE_TAP_THRESHOLD,
  INTERACTION_TAP_MAX_DURATION,
} from '../../constants';
import {
  createDoubleTapZoomProtection,
  type DoubleTapZoomProtection,
} from '../../utils/mapInteractionHelpers';
import type { MapLayerTouchEvent } from 'maplibre-gl';
import type { MapLayerDoubleTapOptions } from './types';

/**
 * Hook for detecting double-tap gesture on MapLibre layers
 *
 * @param layerId - ID of the MapLibre layer to listen for events
 * @param options - Double-tap configuration options
 */
export const useMapLayerDoubleTap = (
  layerId: string,
  options: MapLayerDoubleTapOptions
): void => {
  const {
    threshold = INTERACTION_DOUBLE_TAP_THRESHOLD,
    maxTapDuration = INTERACTION_TAP_MAX_DURATION,
    enabled = true,
    onDoubleTap,
    onSingleTap,
    preventZoom = false,
  } = options;

  const { current: map } = useMap();

  // Refs for double-tap detection
  const lastTapTimeRef = useRef<number>(0);
  const touchStartTimeRef = useRef<number>(0);

  // Double-tap zoom protection (mobile)
  const doubleTapProtectionRef = useRef<DoubleTapZoomProtection | null>(null);

  /**
   * Setup MapLibre layer event listeners
   */
  useEffect(() => {
    if (!map || !enabled) return;

    /**
     * Handle touch start on layer
     */
    const handleTouchStart = () => {
      // Record touch start time for tap duration calculation
      touchStartTimeRef.current = Date.now();

      // Protect against double-tap zoom if enabled
      if (preventZoom) {
        // Initialize protection controller lazily
        if (!doubleTapProtectionRef.current) {
          doubleTapProtectionRef.current = createDoubleTapZoomProtection(map.getMap());
        }
        doubleTapProtectionRef.current.protect();
      }
    };

    /**
     * Handle touch end on layer
     */
    const handleTouchEnd = (e: MapLayerTouchEvent) => {
      // Check if this was a tap (short touch) or long-press
      const touchDuration = Date.now() - touchStartTimeRef.current;
      if (touchDuration > maxTapDuration) {
        // Long-press detected, ignore for double-tap
        // Long-press is handled by useMapLayerLongPress
        return;
      }

      const now = Date.now();
      const timeSinceLastTap = now - lastTapTimeRef.current;

      if (timeSinceLastTap < threshold && timeSinceLastTap > 0) {
        // Double-tap detected!
        e.preventDefault();
        e.originalEvent.stopPropagation();

        lastTapTimeRef.current = 0; // Reset for next detection
        onDoubleTap();
      } else {
        // First tap - record time
        lastTapTimeRef.current = now;

        // Call single-tap callback if provided
        onSingleTap?.();
      }
    };

    // Register MapLibre layer events
    map.on('touchstart', layerId, handleTouchStart);
    map.on('touchend', layerId, handleTouchEnd);
    map.on('touchcancel', layerId, handleTouchEnd);

    // Cleanup
    return () => {
      map.off('touchstart', layerId, handleTouchStart);
      map.off('touchend', layerId, handleTouchEnd);
      map.off('touchcancel', layerId, handleTouchEnd);

      // Cleanup double-tap zoom protection
      doubleTapProtectionRef.current?.cleanup();
    };
  }, [map, layerId, enabled, threshold, maxTapDuration, onDoubleTap, onSingleTap, preventZoom]);
};
